import { app, env, features, withBasePath } from '@ircf/config';

/**
 * Progressive Web App + Web Push helpers.
 *
 * Vite-plugin-pwa can replace the hand-written service worker later; the
 * install prompt and push subscription contract stay the same.
 */

const DISMISS_KEY = 'ircf.pwa.installDismissedAt';
const DISMISS_DAYS = 14;

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const media = window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone =
    'standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  return media || iosStandalone;
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/.test(ua);
  const chromeIos = /CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && webkit && !chromeIos;
}

export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches || isIosSafari();
}

export function wasInstallDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

export function dismissInstallPrompt(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Private mode - ignore.
  }
}

/** Registers `/sw.js` under the app base path when PWA is enabled. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!features.pwa) return null;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

  const scriptUrl = withBasePath('/sw.js');
  try {
    const registration = await navigator.serviceWorker.register(scriptUrl, {
      scope: `${app.basePath}/`,
    });
    return registration;
  } catch (error) {
    console.warn('[pwa] Service worker registration failed.', error);
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output;
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export async function subscribeToPush(
  applicationServerKey: string,
): Promise<PushSubscription | null> {
  if (!features.pushNotifications || !pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(applicationServerKey) as BufferSource,
  });
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();
}

export function vapidPublicKey(): string | null {
  return env.vapidPublicKey;
}

export function serialisePushSubscription(subscription: PushSubscription): {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  expirationTime: number | null;
  userAgent: string | null;
} {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
    expirationTime: subscription.expirationTime,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };
}
